import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ROLE_LABELS } from '@/lib/invites';
import type { MemberRole } from '@/types/database';
import { Loader2, Users, CheckCircle2, XCircle, Clock, LogIn } from 'lucide-react';
import { toast } from 'sonner';

type PreviewStatus =
  | 'loading'
  | 'valid'
  | 'already_member'
  | 'accepted'
  | 'expired'
  | 'not_found'
  | 'unauthenticated'
  | 'error';

interface Preview {
  status: PreviewStatus;
  companyId: string | null;
  companyName: string | null;
  role: MemberRole | null;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-5 p-6 text-center">
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

function IconCircle({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'primary' | 'muted' | 'success' | 'error' | 'warning' }) {
  const toneClass =
    tone === 'primary'
      ? 'bg-primary/10 text-primary'
      : tone === 'success'
        ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
        : tone === 'error'
          ? 'bg-destructive/10 text-destructive'
          : tone === 'warning'
            ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
            : 'bg-muted text-muted-foreground';
  return (
    <div className={`flex h-16 w-16 items-center justify-center rounded-full ${toneClass}`}>
      {children}
    </div>
  );
}

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { refreshCompanies, setActiveCompanyId } = useCompany();

  const [preview, setPreview] = useState<Preview>({
    status: 'loading',
    companyId: null,
    companyName: null,
    role: null,
  });
  const [accepting, setAccepting] = useState(false);

  const nextPath = `/convite/${token ?? ''}`;

  const loadPreview = useCallback(async () => {
    if (!token) {
      setPreview({ status: 'not_found', companyId: null, companyName: null, role: null });
      return;
    }
    try {
      const { data, error } = await supabase.rpc('preview_company_invite', { p_token: token });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : null;
      if (!row) {
        setPreview({ status: 'not_found', companyId: null, companyName: null, role: null });
        return;
      }
      setPreview({
        status: (row.status as PreviewStatus) ?? 'error',
        companyId: row.company_id,
        companyName: row.company_name,
        role: (row.role as MemberRole) ?? null,
      });
    } catch (e) {
      console.error('[AcceptInvitePage] preview error', e);
      setPreview({ status: 'error', companyId: null, companyName: null, role: null });
    }
  }, [token]);

  // Load preview once auth resolves and the user is logged in.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setPreview((p) => ({ ...p, status: 'unauthenticated' }));
      return;
    }
    loadPreview();
  }, [authLoading, user, loadPreview]);

  const handleAccept = useCallback(async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const { data, error } = await supabase.rpc('accept_company_invite', { p_token: token });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : null;
      const status = row?.status ?? 'error';

      if (status === 'accepted' || status === 'already_member') {
        await refreshCompanies();
        if (row?.company_id) setActiveCompanyId(row.company_id);
        toast.success(
          status === 'accepted' ? 'Você entrou na equipe!' : 'Você já faz parte desta equipe.',
        );
        navigate('/dashboard', { replace: true });
        return;
      }

      if (status === 'expired') {
        toast.warning('Este convite expirou.');
        setPreview((p) => ({ ...p, status: 'expired' }));
      } else if (status === 'accepted_by_other') {
        toast.warning('Este convite já foi utilizado.');
        setPreview((p) => ({ ...p, status: 'accepted' }));
      } else if (status === 'not_found') {
        setPreview((p) => ({ ...p, status: 'not_found' }));
      } else {
        toast.error('Não foi possível aceitar o convite.');
        setPreview((p) => ({ ...p, status: 'error' }));
      }
    } catch (e) {
      console.error('[AcceptInvitePage] accept error', e);
      toast.error('Não foi possível aceitar o convite.');
    } finally {
      setAccepting(false);
    }
  }, [token, refreshCompanies, setActiveCompanyId, navigate]);

  // --- Rendering ---------------------------------------------------------

  if (authLoading || preview.status === 'loading') {
    return (
      <Shell>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Carregando convite…</p>
      </Shell>
    );
  }

  if (preview.status === 'unauthenticated') {
    return (
      <Shell>
        <IconCircle tone="primary">
          <Users className="h-8 w-8" />
        </IconCircle>
        <div className="space-y-2">
          <h1 className="text-xl font-bold">Convite para uma equipe</h1>
          <p className="text-sm text-muted-foreground">
            Entre ou crie sua conta no Orçamigo para aceitar este convite.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2">
          <Button3D onClick={() => navigate(`/auth?next=${encodeURIComponent(nextPath)}`)}>
            <LogIn className="mr-2 h-4 w-4" />
            Entrar / criar conta
          </Button3D>
        </div>
      </Shell>
    );
  }

  if (preview.status === 'valid') {
    return (
      <Shell>
        <IconCircle tone="primary">
          <Users className="h-8 w-8" />
        </IconCircle>
        <div className="space-y-2">
          <h1 className="text-xl font-bold">Você foi convidado!</h1>
          <p className="text-sm text-muted-foreground">
            Para colaborar em <span className="font-semibold text-foreground">{preview.companyName}</span>.
          </p>
          {preview.role && (
            <div className="flex justify-center pt-1">
              <Badge variant="outline">Cargo: {ROLE_LABELS[preview.role]}</Badge>
            </div>
          )}
        </div>
        <div className="flex w-full flex-col gap-2">
          <Button3D onClick={handleAccept} disabled={accepting}>
            {accepting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            )}
            Aceitar convite
          </Button3D>
          <Button3D color="gray" onClick={() => navigate('/dashboard')} disabled={accepting}>
            Agora não
          </Button3D>
        </div>
      </Shell>
    );
  }

  if (preview.status === 'already_member') {
    return (
      <Shell>
        <IconCircle tone="success">
          <CheckCircle2 className="h-8 w-8" />
        </IconCircle>
        <div className="space-y-2">
          <h1 className="text-xl font-bold">Você já está na equipe</h1>
          <p className="text-sm text-muted-foreground">
            Você já faz parte de{' '}
            <span className="font-semibold text-foreground">{preview.companyName}</span>.
          </p>
        </div>
        <Button3D
          className="w-full"
          onClick={() => {
            if (preview.companyId) setActiveCompanyId(preview.companyId);
            navigate('/dashboard');
          }}
        >
          Ir para o app
        </Button3D>
      </Shell>
    );
  }

  const terminal: Record<string, { title: string; body: string; icon: React.ReactNode; tone: 'error' | 'warning' }> = {
    expired: {
      title: 'Convite expirado',
      body: 'Este convite não é mais válido. Peça um novo link ao administrador da empresa.',
      icon: <Clock className="h-8 w-8" />,
      tone: 'warning',
    },
    accepted: {
      title: 'Convite já utilizado',
      body: 'Este convite já foi aceito e não pode ser usado novamente.',
      icon: <XCircle className="h-8 w-8" />,
      tone: 'warning',
    },
    not_found: {
      title: 'Convite inválido',
      body: 'Não encontramos este convite. Confira se o link está completo.',
      icon: <XCircle className="h-8 w-8" />,
      tone: 'error',
    },
    error: {
      title: 'Algo deu errado',
      body: 'Não foi possível carregar o convite. Tente novamente em instantes.',
      icon: <XCircle className="h-8 w-8" />,
      tone: 'error',
    },
  };

  const t = terminal[preview.status] ?? terminal.error;

  return (
    <Shell>
      <IconCircle tone={t.tone}>{t.icon}</IconCircle>
      <div className="space-y-2">
        <h1 className="text-xl font-bold">{t.title}</h1>
        <p className="text-sm text-muted-foreground">{t.body}</p>
      </div>
      <Button variant="outline" className="w-full" onClick={() => navigate('/dashboard')}>
        Ir para o app
      </Button>
    </Shell>
  );
}
